import {
    AlertTitle,
    Box,
    Flex,
    HStack,
    Spacer,
    Tooltip,
    useDisclosure,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useState } from 'react';
import { TbTrash } from 'react-icons/tb';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { postActivateNumbers, postValidateNumbers } from 'API/TPMTrial';

import {
    Alert,
    AlertIcon,
    Button,
    Card,
    CardBody,
    Text,
} from '@/components/v4';
import { useTpmActions, useTpmContext } from '../';
import { ActivateNumbers as ActivateNumbersTable } from './ActivateNumbers';
import DisplayMessages from './DisplayMessages';
import EditNumbersModal from './EditNumbersModal';
import {
    clearErrors,
    groupMessages,
    initValidation,
    NUMBER_EXCEED_LIMIT,
} from './helper';

import useGetCurrentCompany from '@/queries/company/useGetCurrentCompany';
/* Activate Numbers Table */
function AssociateNumbersCard({
    associatedAccounts,
    darkMode,
    onClose: onTPMClose,
}) {
    const queryClient = useQueryClient();
    const {
        tpmActivatedNumber: [telephoneNumbers, setTelephoneNumbers],
        tpmDeactivateNumber: [deactivateNumbers, setDeactivateNumbers],
    } = useTpmContext();
    const actions = useTpmActions();
    const [filteredData, setFilteredData] = useState([]);
    const [numberCopy, setNumberCopy] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [validateNumbers, setValidateNumbers] = useState([initValidation]);
    const [formState, setFormState] = useState('idle');
    const [activateList, setActivateList] = useState([]);
    const [modalTitle, setModalTitle] = useState('');

    const { id } = useSelector((state) => state?.modal?.state?.service || {});
    const {
        isOpen: isModalOpen,
        onOpen: onModalOpen,
        onClose: onModalClose,
    } = useDisclosure();
    const { onClose: onDmodalClose } = useDisclosure();
    const { mutate, status: verifiedStatus } = useVerifyNumbers();
    const { mutate: importNumbers, status: importStatus } = useImport();
    const { data: companyData } = useGetCurrentCompany();
    const isTrialExpired =
        companyData?.isTrial &&
        new Date(companyData?.trialEnd + 'Z') < new Date();

    function useImport() {
        return useMutation({
            mutationFn: async (data) => {
                try {
                    onModalOpen();
                    return await postActivateNumbers({
                        serviceID: id,
                        phoneNumbers: data,
                    });
                } catch (error) {
                    throw new Error(
                        error?.message || 'Error importing numbers',
                    );
                }
            },
        });
    }

    function useVerifyNumbers() {
        return useMutation({
            mutationFn: async (data) => {
                try {
                    return await postValidateNumbers({
                        serviceID: id,
                        phoneNumbers: data,
                    });
                } catch (error) {
                    throw new Error(
                        error?.message || 'Error verifying numbers',
                    );
                }
            },
        });
    }
    const handleVerify = () => {
        const telephoneList = [
            ...new Map(
                telephoneNumbers
                    .filter((item) => item.number.trim() !== '')
                    .map((item) => ({
                        ...item,
                        number: item.number.replace(/\D/g, ''),
                    }))
                    .map((item) => [item.number, item]),
            ).values(),
        ];

        mutate(
            telephoneList.map((item) => item.number),
            {
                onSuccess: async (res) => {
                    const data = await res.data;
                    const vTelNumbers = telephoneList.map((item) => {
                        const foundData = data.find(
                            (d) => d.number === item.number,
                        );
                        if (foundData) {
                            return {
                                ...item,
                                status: foundData.message && 'FAIL',
                            };
                        }
                        return {
                            ...item,
                            status: 'PASS',
                        };
                    });

                    setTelephoneNumbers(vTelNumbers);
                    setValidateNumbers(data);
                },
                onError: async (error) => {
                    toast.error('Error Verifying Numbers', { autoClose: 1500 });
                    if (error?.message.includes('400')) {
                        setErrorMessage(
                            'Cannot exceed a total of 5 numbers while Company is on Trial.',
                        );
                        const updatedTelephoneList = telephoneList.map(
                            (item) => ({
                                ...item,
                                status:
                                    item.status !== 'PASS'
                                        ? 'FAIL'
                                        : item.status || 'PASS',
                            }),
                        );
                        setTelephoneNumbers(updatedTelephoneList);
                    } else {
                        // handle other errors
                        const failedTelephoneList = telephoneList.map(
                            (item) => ({
                                ...item,
                                status: 'FAIL',
                            }),
                        );
                        setTelephoneNumbers(failedTelephoneList);
                    }
                },
            },
        );
    };

    const handleSubmit = () => {
        setFormState('submitting');

        const activateNumbers = telephoneNumbers
            .filter((item) => item.status === 'PASS')
            .map((item) => item.number);
        importNumbers(activateNumbers, {
            onSuccess: () => {
                setFormState('finish');
                setActivateList(activateNumbers);
                setTelephoneNumbers([{ number: '', status: '' }]);
                // Invalidate both queries after number activation succeeds
                queryClient.invalidateQueries({ queryKey: ['numbers'] });
                queryClient.invalidateQueries({
                    queryKey: ['getCompanyPlatformNumbers'],
                });
                onTPMClose();
            },
            onError: (error) => {
                setFormState('error');
                setActivateList(activateNumbers);
                throw new Error(error);
            },
        });
    };

    const handleStartOver = () => {
        // Reset TPM state and go back to manage accounts
        actions.reset();
        isModalOpen ? onModalClose() : onDmodalClose();
    };

    const handleClearErrors = () => {
        setErrorMessage('');
        clearErrors(setTelephoneNumbers, setValidateNumbers);
    };
    const onSubmit = (e) => {
        e.preventDefault();
        handleSubmit();
    };

    const groupedMessages =
        verifiedStatus === 'success' &&
        validateNumbers.length >= 1 &&
        groupMessages(validateNumbers);

    useEffect(() => {
        if (importStatus === 'loading') {
            setModalTitle('Associate Request Pending');
        }

        if (formState === 'error' && importStatus === 'error') {
            setModalTitle('Associate Request Error');
        }

        if (formState === 'finish' && importStatus === 'success') {
            setModalTitle('Associate Request Sent');
        }
    }, [formState, importStatus]);

    useEffect(() => {
        setFilteredData(telephoneNumbers);
    }, [telephoneNumbers, deactivateNumbers, setDeactivateNumbers]);

    useEffect(() => {
        if (onTPMClose) {
            setTelephoneNumbers([{ number: '', status: '' }]);
            setDeactivateNumbers([{ number: '', status: '' }]);
        }
    }, [onTPMClose, setTelephoneNumbers, setDeactivateNumbers]);
    return associatedAccounts?.length > 0 && !isTrialExpired ? (
        <Fragment>
            <Card
                as="form"
                onSubmit={onSubmit}
                bg={darkMode ? 'dark.bgDarkGray' : 'white'}
                width="full"
                mt={12}>
                <CardBody>
                    <Flex width="full" className="mt-0">
                        <Flex
                            justifyContent="flex-start"
                            flexDir="column"
                            width="full"
                            className="mt-1">
                            <Text className="text-xl font-semibold">
                                Associate Numbers
                            </Text>
                            <p className="mt-[12px] text-xs font-medium leading-none tracking-tight text-[#8e8e8e]">
                                Adds Telephone Numbers from Associated Accounts.
                                Add numbers below. 1 per line, max 1000 lines.
                            </p>
                        </Flex>
                    </Flex>
                    <Box color={darkMode ? 'white' : 'black'}>
                        {verifiedStatus === 'error' && errorMessage && (
                            <Alert
                                status="error"
                                flexDir="column"
                                alignItems="flex-start"
                                mt={4}>
                                <Flex width="100%" alignItems="center">
                                    <AlertIcon />
                                    <AlertTitle>Verification Error!</AlertTitle>
                                    <Spacer />
                                    <Button
                                        justifyContent="flex-end"
                                        ml={14}
                                        variant="link"
                                        h="24px"
                                        color={darkMode ? 'white' : 'black'}
                                        leftIcon={<TbTrash />}
                                        isDisabled={
                                            !telephoneNumbers?.some(
                                                (item) =>
                                                    item.status === 'FAIL',
                                            )
                                        }
                                        onClick={handleClearErrors}>
                                        Clear Errors
                                    </Button>
                                </Flex>
                                {errorMessage}
                            </Alert>
                        )}
                        {verifiedStatus === 'success' &&
                            validateNumbers?.length >= 1 &&
                            telephoneNumbers.find(
                                (n) => n.status === 'FAIL',
                            ) && (
                                <Alert
                                    status="error"
                                    flexDir="column"
                                    alignItems="flex-start"
                                    mt={4}>
                                    <Flex width="100%" alignItems="center">
                                        <AlertIcon />
                                        <AlertTitle>
                                            Verification Error!
                                        </AlertTitle>
                                        <Spacer />
                                        <Button
                                            justifyContent="flex-end"
                                            ml={14}
                                            variant="link"
                                            h="24px"
                                            color={darkMode ? 'white' : 'black'}
                                            leftIcon={<TbTrash />}
                                            isDisabled={
                                                !telephoneNumbers?.some(
                                                    (item) =>
                                                        item.status === 'FAIL',
                                                )
                                            }
                                            onClick={handleClearErrors}>
                                            Clear Errors
                                        </Button>
                                    </Flex>
                                    <DisplayMessages
                                        groupedMessages={groupedMessages}
                                    />
                                </Alert>
                            )}

                        {telephoneNumbers?.length > 1000 && (
                            <Alert
                                status="error"
                                flexDir="column"
                                alignItems="flex-start"
                                mt={4}>
                                <Flex>
                                    <AlertIcon />
                                    <AlertTitle>Error!</AlertTitle>
                                    {NUMBER_EXCEED_LIMIT}
                                </Flex>
                            </Alert>
                        )}
                    </Box>

                    <ActivateNumbersTable
                        type="activate"
                        data={filteredData ? filteredData : telephoneNumbers}
                        setData={setTelephoneNumbers}
                        verifiedStatus={verifiedStatus}
                        numberCopy={numberCopy}
                        setNumberCopy={setNumberCopy}
                    />
                    <HStack spacing={6} paddingTop={5}>
                        <Tooltip
                            hasArrow
                            shouldWrapChildren
                            mt="1"
                            label={
                                telephoneNumbers?.some(
                                    (item) => item.number === '',
                                )
                                    ? 'Please fill in all numbers'
                                    : ''
                            }>
                            <Button
                                colorScheme="whatsapp"
                                minW="128px"
                                px={2}
                                onClick={handleVerify}>
                                {verifiedStatus === 'loading'
                                    ? 'Verifying'
                                    : 'Verify'}
                            </Button>
                        </Tooltip>
                        <Tooltip
                            hasArrow
                            shouldWrapChildren
                            mt="1"
                            label={
                                telephoneNumbers?.some(
                                    (item) => item.status !== 'PASS',
                                )
                                    ? 'Please verify numbers and clear errors before importing'
                                    : ''
                            }>
                            <Button
                                type="submit"
                                minW="128px"
                                isDisabled={
                                    telephoneNumbers?.some(
                                        (item) => item.status !== 'PASS',
                                    ) || importStatus === 'loading'
                                }>
                                Associate
                            </Button>
                        </Tooltip>
                    </HStack>
                </CardBody>
            </Card>

            <EditNumbersModal
                isOpen={isModalOpen}
                onClose={handleStartOver}
                status={importStatus}
                modalTitle={modalTitle}
                numberList={activateList}
            />
        </Fragment>
    ) : null;
}

export default AssociateNumbersCard;
